/** @jsxImportSource @emotion/react */

import React, {useState, useEffect} from "react";
import Immutable from "immutable";
import {css} from "@emotion/react";

import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as TagRepo from "js/common/repo/backbone/tag-repo";
import * as Users from "js/common/users";
import * as Groups from "js/common/groups";
import * as Ajax from "js/common/ajax";
import * as SavedConfigs from "js/common/saved-configs";
import {indexBy} from "js/common/utils/collections";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {bullhornLightBlue, bullhornOrange} from "js/common/cube19-colors";

const printContainerStyle = css`
  @media print {
    position: relative;
    padding: 0;
    margin-top: 10px;
    display: block;
  }
  @media screen {
    display: none;
  }
`;

const printRowStyle = css`
  @media print {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }
`;

const printFullRowStyle = css`
  @media print {
    display: block;
    margin-bottom: 12px;
    box-shadow: 1px 1px 2px rgba(0,0,0,0.1);
  }
`;

const printItemStyle = theme => css`
  @media print {
    padding: 8px;
    background-color: ${theme.palette.background.card};
    box-shadow: 1px 1px 2px rgba(0,0,0,0.1);
    border-radius: 4px;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const printLabelStyle = theme => css`
  @media print {
    font-weight: 600;
    font-size: 12px;
    display: inline-block;
    white-space: nowrap;
    font-family: ${theme.typography.fontFamily};
  }
`;

const printValueStyle = css`
  @media print {
    word-break: break-word;
    font-size: 11px;
    flex: 1;
  }
`;

const printChipContainerStyle = css`
  @media print {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
`;

const printItemEmptyStyle = theme => css`
  @media print {
    padding: 8px;
    background-color: ${theme.palette.background.card};
    border-radius: 4px;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }
`;

const printItemPopulatedStyle = theme => css`
  @media print {
    padding: 8px;
    background-color: ${theme.palette.background.card};
    border-radius: 4px;
    font-size: 11px;
    display: block;
    font-family: ${theme.typography.fontFamily};
  }
`;

const getTagOptions = () => Immutable
    .fromJS(TagRepo.getAll().toJSON())
    .map(tag => {
      const category = Immutable.fromJS(TagRepo.getCategory(tag.get("categoryId")).toJSON());
      return Immutable.Map({
        categoryName: category.get("name"),
        name: tag.get("name"),
        id: tag.get("id")
      });
    });

const PrintDataConfigToolbar = React.memo(({config}) => {
  const [clientData, setClientData] = useState(Immutable.Map());
  const [clientSetData, setClientSetData] = useState(Immutable.Map());

  const {theme} = React.useContext(CustomThemeContext);

  const qualifier = config.get("qualifier", Immutable.Map());
  const timeframe = config.get("timeframe", Immutable.Map());
  const clientFilter = config.get("clientFilter", Immutable.Map());
  const tagFilter = config.get("tagFilter", Immutable.Map());

  // Fetch client and client set data on mount or when filter changes
  // (to display names instead of IDs)
  useEffect(() => {
    const clientIds = clientFilter.get("clientIds", Immutable.Set());
    const clientSetIds = clientFilter.get("clientSetIds", Immutable.Set());

    if (!clientIds.isEmpty()) {
      Ajax.post({url: "entity-finder", json: {entityType: "CLIENT", ids: clientIds.toJS()}})
          .then(results => {
            // Convert array of results to Immutable List, then map each to Immutable Map
            const clientsList = Immutable.List(results).map(c => Immutable.fromJS(c));
            const idToClient = indexBy(c => String(c.get("id")), clientsList);
            setClientData(idToClient);
          })
          .catch(() => {
            // Handle error silently
          });
    }

    if (!clientSetIds.isEmpty()) {
      SavedConfigs.getAll("CLIENT_SET")
          .then(ownershipTypeToSavedClientSets => {
            const allClientSets = ownershipTypeToSavedClientSets
                .toList()
                .flatMap(sets => sets);
            const idToClientSet = indexBy(cs => cs.get("id"), allClientSets);
            setClientSetData(idToClientSet);
          })
          .catch(() => {
            // Handle error silently
          });
    }
  }, [clientFilter]);

  const getQualifierDisplay = () => {
    const type = qualifier.get("type");
    const id = qualifier.get("id");

    if (!type || id === undefined || id === null) return "All Users";

    if (type === "USER") {
      if (id === 0) {
        return "Logged in User";
      }
      const user = Users.getUser(id);
      return user ? user.get("firstName") + " " + user.get("lastName") : "User #" + id;
    } else if (type === "GROUP") {
      if (id === 0) {
        return "Logged in User's Group";
      }
      const group = Groups.getGroup(id);
      return group ? group.get("name") : "Group #" + id;
    }
    return "Unknown";
  };

  const getTimeframeDisplay = () => {
    const timeframeObj = timeframe.toJS ? timeframe.toJS() : timeframe;
    if (!timeframeObj || !timeframeObj.type) return "N/A";

    const parsed = TimeframeRepo.parse(timeframeObj);
    if (!parsed) return "N/A";

    const name = parsed.get("name");
    const start = parsed.get("start");
    const end = parsed.get("end");

    // Build date range string if both dates exist
    let dateRangeStr = "";
    if (start && end) {
      const startStr = start.format("D MMM YYYY");
      const endStr = end.format("D MMM YYYY");
      dateRangeStr = ` (${startStr} to ${endStr})`;
    }

    // Return name with dates, or just dates, or N/A
    if (name) {
      return name + dateRangeStr;
    }

    if (dateRangeStr) {
      return dateRangeStr.substring(2, dateRangeStr.length - 1); // Remove parentheses if no name
    }

    return "N/A";
  };

  const getClientFilterDisplay = () => {
    const clientIds = clientFilter.get("clientIds", Immutable.Set());
    const clientSetIds = clientFilter.get("clientSetIds", Immutable.Set());

    if (clientIds.isEmpty() && clientSetIds.isEmpty()) {
      return <span style={{fontSize: "11px"}}>No filters applied</span>;
    }

    return <div css={printChipContainerStyle}>
      {clientIds.map(id => {
        const client = clientData.get(String(id));
        const displayName = client?.get("name") || `Client #${id}`;

        return (
            <div key={`client-${id}`} style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "3px",
              padding: "4px 8px",
              fontSize: "11px",
              fontFamily: theme.typography.fontFamily
            }}>
              <span style={{
                display: "inline-block",
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: bullhornLightBlue
              }} />
              {displayName}
            </div>
        );
      })}
      {clientSetIds.map(id => {
        const clientSet = clientSetData.get(id);
        const displayName = clientSet ? clientSet.get("name") : "Client Set #" + id;
        return (
            <div key={`clientset-${id}`} style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "3px",
              padding: "4px 8px",
              fontSize: "11px",
              fontFamily: theme.typography.fontFamily
            }}>
              <span style={{
                display: "inline-block",
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: bullhornOrange
              }} />
              {displayName}
            </div>
        );
      })}
    </div>;
  };

  const getTagFilterDisplay = () => {
    const matchAnyTagIds = tagFilter.get("matchAnyTagIds", Immutable.Set());
    const matchAllTagIds = tagFilter.get("matchAllTagIds", Immutable.Set());
    const excludedTagIds = tagFilter.get("excludedTagIds", Immutable.Set());

    const tagOptions = getTagOptions();
    const idToTagOption = indexBy(t => t.get("id"), tagOptions);

    const items = [];

    if (!matchAnyTagIds.isEmpty()) {
      items.push({
        label: "Match Any",
        count: matchAnyTagIds.size,
        tags: matchAnyTagIds.map(id => idToTagOption.get(id)).filter(Boolean),
        color: "#E1FFB0",
        textColor: "rgba(0, 0, 0, 0.5)"
      });
    }

    if (!matchAllTagIds.isEmpty()) {
      items.push({
        label: "Match All",
        count: matchAllTagIds.size,
        tags: matchAllTagIds.map(id => idToTagOption.get(id)).filter(Boolean),
        color: "#B0ECFF",
        textColor: "rgba(0, 0, 0, 0.5)"
      });
    }

    if (!excludedTagIds.isEmpty()) {
      items.push({
        label: "Excluding",
        count: excludedTagIds.size,
        tags: excludedTagIds.map(id => idToTagOption.get(id)).filter(Boolean),
        color: "#FFCDB0",
        textColor: "rgba(0, 0, 0, 0.5)"
      });
    }

    if (items.length === 0) return <span style={{fontSize: "11px"}}>No tags applied</span>;

    return items.map(item => {
      const typeLabel = item.label === "Match Any" ? "Any" : item.label === "Match All" ? "All" : "Exclude";
      const displayLabel = item.label === "Match Any" ? "Matches Any" : item.label === "Match All" ? "Matches All" : "Excluding";

      return (
        <div key={item.label} style={{display: "flex", alignItems: "center", gap: "10px", marginTop: "8px"}}>
          <div style={{fontSize: "11px", fontWeight: "500", marginBottom: "4px", fontFamily: theme.typography.fontFamily, whiteSpace: "nowrap", flexShrink: 0}}>{displayLabel} ({item.count}):</div>
          <div css={printChipContainerStyle}>
            <div
                style={{
                  display: "inline-block",
                  backgroundColor: item.color,
                  borderRadius: "10px",
                  padding: "2px 4px",
                  fontSize: "10px",
                  color: item.textColor,
                  fontWeight: "600",
                  minWidth: "50px",
                  textAlign: "center",
                  fontFamily: theme.typography.fontFamily
                }}
            >
              {typeLabel}
            </div>
            {item.tags.map(tag => (
                <div
                    key={tag.get("id")}
                    style={{
                      display: "inline-block",
                      backgroundColor: "#f0f0f0",
                      borderRadius: "3px",
                      padding: "2px 6px",
                      fontSize: "10px",
                      color: "rgba(0, 0, 0, 0.7)",
                      fontFamily: theme.typography.fontFamily
                    }}
                >
                  <strong>{tag.get("categoryName")}</strong>: {tag.get("name")}
                </div>
            ))}
          </div>
        </div>
      );
    });
  };

  const clientsEmpty = clientFilter.get("clientIds", Immutable.Set()).isEmpty() && clientFilter.get("clientSetIds", Immutable.Set()).isEmpty();
  const tagsEmpty = tagFilter.get("matchAnyTagIds", Immutable.Set()).isEmpty() && tagFilter.get("matchAllTagIds", Immutable.Set()).isEmpty() && tagFilter.get("excludedTagIds", Immutable.Set()).isEmpty();
  const bothEmpty = clientsEmpty && tagsEmpty;

  return (
      <div css={printContainerStyle}>
        <div css={printRowStyle}>
          <div css={printItemStyle(theme)}>
            <div css={printValueStyle}>{getQualifierDisplay()}</div>
          </div>
          <div css={printItemStyle(theme)}>
            <div css={printValueStyle}>{getTimeframeDisplay()}</div>
          </div>
        </div>
        {bothEmpty ? (
          <div css={printRowStyle}>
            <div css={printItemEmptyStyle(theme)}>
              <div css={printLabelStyle(theme)}><i className="bhi-company"/> Clients & Client Sets (0)</div>
              <div css={printValueStyle}>{getClientFilterDisplay()}</div>
            </div>
            <div css={printItemEmptyStyle(theme)}>
              <div css={printLabelStyle(theme)}><i className="bhi-category-tags"/> Tags (0)</div>
              <div css={printValueStyle}>{getTagFilterDisplay()}</div>
            </div>
          </div>
        ) : (
          <>
            <div css={printFullRowStyle}>
              <div css={clientsEmpty ? printItemEmptyStyle(theme) : printItemPopulatedStyle(theme)}>
                <div css={printLabelStyle(theme)}><i className="bhi-company"/> Clients & Client Sets ({clientFilter.get("clientIds", Immutable.Set()).size + clientFilter.get("clientSetIds", Immutable.Set()).size})</div>
                <div css={printValueStyle}>{getClientFilterDisplay()}</div>
              </div>
            </div>
            <div css={printFullRowStyle}>
              <div css={tagsEmpty ? printItemEmptyStyle(theme) : printItemPopulatedStyle(theme)}>
                <div css={printLabelStyle(theme)}><i className="bhi-category-tags"/> Tags ({tagFilter.get("matchAnyTagIds", Immutable.Set()).size + tagFilter.get("matchAllTagIds", Immutable.Set()).size + tagFilter.get("excludedTagIds", Immutable.Set()).size})</div>
                <div css={printValueStyle}>{getTagFilterDisplay()}</div>
              </div>
            </div>
          </>
        )}
      </div>
  );
});

export default PrintDataConfigToolbar;

